import { ChunkDataProvider, SceneId, TilesetData } from './api';
import { FacingDirection } from './components/components';
import { getRectMtv, Point, Rect } from './geometryUtils';

/**
 * Result of a map collision check.
 * Includes the MTV and all tiles the entity is currently colliding with.
 */
export interface MapCollisionResult {
  mtv: Point;
  collidingTiles: Rect[];
}

/**
 * Tile coordinate in world tile space.
 */
export interface TileCoord {
  col: number;
  row: number;
}

/** Max overlap ratio (perpendicular axis) for corner correction to activate. 0.5 = half tile edge length on the perpendicular axis. */
export const CORNER_CORRECTION_THRESHOLD = 0.5;

/**
 * Returns the tile coordinates covered by a simple facing-aware tile cone.
 *
 * The cone is represented as a triangle of tiles with the origin at the
 * observer's current tile and a base that widens by one tile on each side per
 * step away from the observer.
 */
export function getLineOfSightTiles(
  origin: TileCoord,
  facing: FacingDirection,
  tileRange: number,
): TileCoord[] {
  const normalizedRange = Math.max(0, Math.floor(tileRange));
  const tiles: TileCoord[] = [];
  const seen = new Set<string>();

  const addTile = (col: number, row: number): void => {
    const key = `${col},${row}`;
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    tiles.push({ col, row });
  };

  addTile(origin.col, origin.row);

  for (let step = 1; step <= normalizedRange; step++) {
    switch (facing) {
      case 'up': {
        const row = origin.row - step;
        for (let offset = -step; offset <= step; offset++) {
          addTile(origin.col + offset, row);
        }
        break;
      }
      case 'down': {
        const row = origin.row + step;
        for (let offset = -step; offset <= step; offset++) {
          addTile(origin.col + offset, row);
        }
        break;
      }
      case 'left': {
        const col = origin.col - step;
        for (let offset = -step; offset <= step; offset++) {
          addTile(col, origin.row + offset);
        }
        break;
      }
      case 'right': {
        const col = origin.col + step;
        for (let offset = -step; offset <= step; offset++) {
          addTile(col, origin.row + offset);
        }
        break;
      }
    }
  }

  return tiles;
}

/**
 * Checks for collision between a rectangle and the map (tiles and objects).
 * Returns the MTV (Minimum Translation Vector) and colliding tile info if a collision is detected.
 */
export function checkMapCollision(
  provider: ChunkDataProvider,
  entityRect: Rect,
  sceneId: SceneId,
): MapCollisionResult | null | undefined {
  // Get scene metadata for tilesets and tile dimensions
  const sceneMetadata = provider.getSceneMetadata(sceneId);
  const chunks = provider.getChunks(sceneId);

  // Both server and client should have chunk data populated
  if (!sceneMetadata || !chunks) {
    return undefined;
  }

  const tileWidth = sceneMetadata.tileWidth;
  const tileHeight = sceneMetadata.tileHeight;
  const tilesets = sceneMetadata.tilesets;
  const CHUNK_SIZE = sceneMetadata.chunkSize;

  // Calculate tile range the entity overlaps
  const startCol = Math.floor(entityRect.x / tileWidth);
  const endCol = Math.floor((entityRect.x + entityRect.width) / tileWidth);
  const startRow = Math.floor(entityRect.y / tileHeight);
  const endRow = Math.floor((entityRect.y + entityRect.height) / tileHeight);

  let maxTileMtv: Point | null = null;
  let maxTileOverlap = -Infinity;
  const collidingTiles: Rect[] = [];

  // 1. Check world boundaries
  const boundaryResult = checkWorldBoundaries(entityRect, sceneMetadata);

  // 2. Determine which chunks we need to check
  const startChunkX = Math.floor(startCol / CHUNK_SIZE);
  const endChunkX = Math.floor(endCol / CHUNK_SIZE);
  const startChunkY = Math.floor(startRow / CHUNK_SIZE);
  const endChunkY = Math.floor(endRow / CHUNK_SIZE);

  // Check tile collisions in each relevant chunk
  for (let chunkY = startChunkY; chunkY <= endChunkY; chunkY++) {
    for (let chunkX = startChunkX; chunkX <= endChunkX; chunkX++) {
      const chunkKey = `${chunkX},${chunkY}`;
      const chunkData = chunks.get(chunkKey);

      if (!chunkData) {
        // If a chunk is missing within the overlap range, we treat the entire chunk
        // area as blocking. This prevents 'holes' in infinite maps from being walkable.
        const chunkRect: Rect = {
          x: chunkX * CHUNK_SIZE * tileWidth,
          y: chunkY * CHUNK_SIZE * tileHeight,
          width: CHUNK_SIZE * tileWidth,
          height: CHUNK_SIZE * tileHeight,
        };

        const mtv = getRectMtv(entityRect, chunkRect);
        if (mtv) {
          collidingTiles.push(chunkRect);
          const overlap = mtv.x * mtv.x + mtv.y * mtv.y;
          if (overlap > maxTileOverlap) {
            maxTileOverlap = overlap;
            maxTileMtv = mtv;
          }
        }
        continue;
      }

      // Tile range within this chunk that overlaps with entity
      const chunkStartCol = chunkX * CHUNK_SIZE;
      const chunkStartRow = chunkY * CHUNK_SIZE;
      const localStartCol = Math.max(0, startCol - chunkStartCol);
      const localEndCol = Math.min(CHUNK_SIZE - 1, endCol - chunkStartCol);
      const localStartRow = Math.max(0, startRow - chunkStartRow);
      const localEndRow = Math.min(CHUNK_SIZE - 1, endRow - chunkStartRow);

      for (let localRow = localStartRow; localRow <= localEndRow; localRow++) {
        for (let localCol = localStartCol; localCol <= localEndCol; localCol++) {
          const localTileIndex = localRow * CHUNK_SIZE + localCol;
          const worldCol = chunkStartCol + localCol;
          const worldRow = chunkStartRow + localRow;

          let hasAnyTile = false;
          let isBlocking = false;

          for (const layerName in chunkData.layers) {
            const tileId = chunkData.layers[layerName][localTileIndex];

            if (tileId !== 0 && tileId !== undefined) {
              hasAnyTile = true;
              if (isGidBlocking(tileId, tilesets)) {
                isBlocking = true;
                break;
              }
            }
          }

          // A cell is blocking if it contains a blocking tile OR if it's completely empty
          if (isBlocking || !hasAnyTile) {
            const tileRect: Rect = {
              x: worldCol * tileWidth,
              y: worldRow * tileHeight,
              width: tileWidth,
              height: tileHeight,
            };

            const mtv = getRectMtv(entityRect, tileRect);
            if (mtv) {
              collidingTiles.push(tileRect);
              const overlap = mtv.x * mtv.x + mtv.y * mtv.y;
              if (overlap > maxTileOverlap) {
                maxTileOverlap = overlap;
                maxTileMtv = mtv;
              }
            }
          }
        }
      }
    }
  }

  // Combine boundary check with tile collisions
  if (boundaryResult) {
    return {
      mtv: {
        x: boundaryResult.mtv.x + (maxTileMtv?.x ?? 0),
        y: boundaryResult.mtv.y + (maxTileMtv?.y ?? 0),
      },
      collidingTiles,
    };
  }

  return maxTileMtv ? { mtv: maxTileMtv, collidingTiles } : null;
}

// Simple cache for gid -> blocksWalking lookups to avoid repeated tileset scans
const GID_BLOCKS_CACHE = new Map<number, boolean>();

/**
 * Internal helper to check if a specific GID is blocking, with caching.
 */
function isGidBlocking(tileId: number, tilesets: TilesetData[]): boolean {
  const cached = GID_BLOCKS_CACHE.get(tileId);
  if (cached !== undefined) return cached;

  const tileset = tilesets?.find(
    (ts) => tileId >= ts.firstgid && tileId < ts.firstgid + ts.tilecount,
  );

  let isBlocking = false;
  if (tileset) {
    const localId = tileId - tileset.firstgid;
    isBlocking = !!(tileset.blocksWalking?.[localId] || tileset.collisions?.[localId]);
  }

  GID_BLOCKS_CACHE.set(tileId, isBlocking);
  return isBlocking;
}

/**
 * Checks if a specific tile coordinate is blocking.
 */
export function isTileBlocking(
  provider: ChunkDataProvider,
  sceneId: SceneId,
  col: number,
  row: number,
): boolean {
  const sceneMetadata = provider.getSceneMetadata(sceneId);
  const chunks = provider.getChunks(sceneId);

  if (!sceneMetadata || !chunks) {
    return false;
  }

  const CHUNK_SIZE = sceneMetadata.chunkSize;
  const tilesets = sceneMetadata.tilesets;

  const chunkX = Math.floor(col / CHUNK_SIZE);
  const chunkY = Math.floor(row / CHUNK_SIZE);
  const chunkKey = `${chunkX},${chunkY}`;
  const chunkData = chunks.get(chunkKey);

  if (!chunkData) {
    // If chunk data is missing for prioritized coordinates, we treat it as blocking.
    // This provides consistent behavior for unmapped regions or gaps in chunk coverage.
    return true;
  }

  const localCol = col % CHUNK_SIZE;
  const localRow = row % CHUNK_SIZE;
  // Handle potential negative result from modulo on negative numbers
  const normalizedCol = localCol < 0 ? localCol + CHUNK_SIZE : localCol;
  const normalizedRow = localRow < 0 ? localRow + CHUNK_SIZE : localRow;

  const localTileIndex = normalizedRow * CHUNK_SIZE + normalizedCol;

  let hasAnyTile = false;
  for (const layerName in chunkData.layers) {
    const tileId = chunkData.layers[layerName][localTileIndex];

    if (tileId !== 0 && tileId !== undefined) {
      hasAnyTile = true;
      if (isGidBlocking(tileId, tilesets)) {
        return true;
      }
    }
  }

  // A coordinate is only walkable if it contains at least one non-blocking tile.
  return !hasAnyTile;
}

/**
 * Checks if there is a clear line of sight between two points.
 * Returns true if no blocking tiles are intersected by the line segment.
 *
 * Uses the Amanatides-Woo algorithm for robust grid traversal, ensuring
 * that every tile intersected by the line segment is checked for collision.
 */
export function hasLineOfSight(
  provider: ChunkDataProvider,
  sceneId: SceneId,
  start: Point,
  end: Point,
): boolean {
  const sceneMetadata = provider.getSceneMetadata(sceneId);
  if (!sceneMetadata) return false;

  const { tileWidth, tileHeight } = sceneMetadata;

  let x = Math.floor(start.x / tileWidth);
  let y = Math.floor(start.y / tileHeight);
  const endX = Math.floor(end.x / tileWidth);
  const endY = Math.floor(end.y / tileHeight);

  // Check the starting tile
  if (isTileBlocking(provider, sceneId, x, y)) {
    return false;
  }

  if (x === endX && y === endY) return true;

  const dx = end.x - start.x;
  const dy = end.y - start.y;

  const stepX = dx > 0 ? 1 : -1;
  const stepY = dy > 0 ? 1 : -1;

  // Amount of t required to move one tile width or height
  const tDeltaX = Math.abs(tileWidth / dx);
  const tDeltaY = Math.abs(tileHeight / dy);

  // Initial distance from the starting point to the first tile boundary
  let tMaxX =
    dx === 0
      ? Infinity
      : (Math.floor(start.x / tileWidth + (stepX > 0 ? 1 : 0)) * tileWidth - start.x) / dx;
  let tMaxY =
    dy === 0
      ? Infinity
      : (Math.floor(start.y / tileHeight + (stepY > 0 ? 1 : 0)) * tileHeight - start.y) / dy;

  // Ensure tMax is non-negative (can happen due to floating point precision at boundaries)
  if (tMaxX < 0) tMaxX += tDeltaX;
  if (tMaxY < 0) tMaxY += tDeltaY;

  // Limit traversal to the number of tiles between start and end
  const maxIterations = Math.abs(endX - x) + Math.abs(endY - y) + 1;
  let iterations = 0;

  while (iterations < maxIterations) {
    if (tMaxX < tMaxY) {
      x += stepX;
      tMaxX += tDeltaX;
    } else {
      y += stepY;
      tMaxY += tDeltaY;
    }

    if (isTileBlocking(provider, sceneId, x, y)) {
      return false;
    }

    if (x === endX && y === endY) {
      break;
    }
    iterations++;
  }

  return true;
}

/**
 * Calculates a translation vector to push an entity back within the map boundaries.
 * Returns the MTV and overlap squared if the entity is out of bounds.
 */
function checkWorldBoundaries(
  entityRect: Rect,
  sceneMetadata: {
    minChunkX: number;
    maxChunkX: number;
    minChunkY: number;
    maxChunkY: number;
    chunkSize: number;
    tileWidth: number;
    tileHeight: number;
  },
): { mtv: Point; overlapSq: number } | null {
  const { tileWidth, tileHeight, chunkSize } = sceneMetadata;
  const mapMinX = sceneMetadata.minChunkX * chunkSize * tileWidth;
  const mapMaxX = (sceneMetadata.maxChunkX + 1) * chunkSize * tileWidth;
  const mapMinY = sceneMetadata.minChunkY * chunkSize * tileHeight;
  const mapMaxY = (sceneMetadata.maxChunkY + 1) * chunkSize * tileHeight;

  let boundaryMtvX = 0;
  let boundaryMtvY = 0;

  if (entityRect.x < mapMinX) {
    boundaryMtvX = mapMinX - entityRect.x;
  } else if (entityRect.x + entityRect.width > mapMaxX) {
    boundaryMtvX = mapMaxX - (entityRect.x + entityRect.width);
  }

  if (entityRect.y < mapMinY) {
    boundaryMtvY = mapMinY - entityRect.y;
  } else if (entityRect.y + entityRect.height > mapMaxY) {
    boundaryMtvY = mapMaxY - (entityRect.y + entityRect.height);
  }

  if (boundaryMtvX !== 0 || boundaryMtvY !== 0) {
    return {
      mtv: { x: boundaryMtvX, y: boundaryMtvY },
      overlapSq: boundaryMtvX * boundaryMtvX + boundaryMtvY * boundaryMtvY,
    };
  }

  return null;
}

/**
 * Calculates a position nudge to slide an entity around the corner of a blocking tile.
 * This is only applied during "mostly cardinal" movement to avoid interrupting
 * intentional diagonal movement.
 */
export function calculateCornerCorrection({
  provider,
  sceneId,
  entityRect,
  collidingTiles,
  mtv,
  vel,
  entitySpeed,
  deltaTime,
}: {
  provider: ChunkDataProvider;
  sceneId: SceneId;
  entityRect: Rect;
  collidingTiles: Rect[];
  mtv: { x: number; y: number };
  vel: { dx: number; dy: number };
  entitySpeed: number;
  deltaTime: number;
}): { x: number; y: number } | null {
  // If movement is mostly cardinal, check if we should nudge the entity around a corner.
  // Robust primaryAxis: whichever velocity component is larger.
  const absDx = Math.abs(vel.dx);
  const absDy = Math.abs(vel.dy);
  if (Math.abs(absDx - absDy) < 0.1) return null; // Too diagonal

  const primaryAxis = absDx > absDy ? 'x' : 'y';
  const perpAxis = primaryAxis === 'x' ? 'y' : 'x';

  if (entitySpeed <= 0) return null;

  const sceneMetadata = provider.getSceneMetadata(sceneId);
  if (!sceneMetadata) return null;

  const { tileWidth, tileHeight } = sceneMetadata;
  const perpTileSize = primaryAxis === 'x' ? tileHeight : tileWidth;

  // We track the maximum absolute nudge to avoid summation "super-sliding"
  // when hitting multiple tiles (e.g. wall seams).
  let maxNudgeAmount = 0;
  let finalNudgeX = 0;
  let finalNudgeY = 0;

  for (const tile of collidingTiles) {
    // Calculate perpendicular overlap
    const entityStart = perpAxis === 'x' ? entityRect.x : entityRect.y;
    const entityEnd = entityStart + (perpAxis === 'x' ? entityRect.width : entityRect.height);
    const tileStart = perpAxis === 'x' ? tile.x : tile.y;
    const tileEnd = tileStart + (perpAxis === 'x' ? tile.width : tile.height);

    const overlapStart = Math.max(entityStart, tileStart);
    const overlapEnd = Math.min(entityEnd, tileEnd);
    const perpOverlap = overlapEnd - overlapStart;

    if (perpOverlap <= 0) continue;

    const overlapRatio = perpOverlap / perpTileSize;

    // Only nudge if we are brushing the corner (less than 50% overlap)
    if (overlapRatio < CORNER_CORRECTION_THRESHOLD) {
      // Determine nudge direction
      const entityCenter = (entityStart + entityEnd) / 2;
      const tileCenter = (tileStart + tileEnd) / 2;
      const nudgeDirection = entityCenter < tileCenter ? -1 : 1;

      // Check neighbours to avoid nudging into adjacent walls.
      // We only want to nudge if there's actually open space to slide into.
      const col = Math.floor(tile.x / tileWidth);
      const row = Math.floor(tile.y / tileHeight);

      let neighbourCol = col;
      let neighbourRow = row;
      if (perpAxis === 'x') {
        neighbourCol += nudgeDirection;
      } else {
        neighbourRow += nudgeDirection;
      }

      // 1. Check direct neighbour: Is the tile we are nudging TOWARDS empty?
      if (!isTileBlocking(provider, sceneId, neighbourCol, neighbourRow)) {
        // 2. Check diagonal neighbour: Is the path FORWARD from that nudge also empty?
        // We look "one step ahead" in the primary direction of movement to ensure
        // we aren't nudging the entity into a wall that's just around the corner.
        let diagonalCol = neighbourCol;
        let diagonalRow = neighbourRow;
        const primaryDir = primaryAxis === 'x' ? Math.sign(mtv.x) : Math.sign(mtv.y);

        if (primaryAxis === 'x') {
          diagonalCol += primaryDir;
        } else {
          diagonalRow += primaryDir;
        }

        if (!isTileBlocking(provider, sceneId, diagonalCol, diagonalRow)) {
          // Both tiles are empty, so it's safe to slide around this corner.
          // Apply nudge to position correction.
          const nudgeStrength = (1 - overlapRatio / CORNER_CORRECTION_THRESHOLD) * entitySpeed;
          const nudgeAmount = nudgeDirection * nudgeStrength * deltaTime;

          if (Math.abs(nudgeAmount) > Math.abs(maxNudgeAmount)) {
            maxNudgeAmount = nudgeAmount;
            if (perpAxis === 'x') {
              finalNudgeX = nudgeAmount;
              finalNudgeY = 0;
            } else {
              finalNudgeX = 0;
              finalNudgeY = nudgeAmount;
            }
          }
        }
      }
    }
  }

  return finalNudgeX !== 0 || finalNudgeY !== 0 ? { x: finalNudgeX, y: finalNudgeY } : null;
}
